import { useCallback, useRef, useEffect } from 'react';

// Short double-buzz for a new chat message. Uses the BBC Android bridge when available
// (WebView blocks navigator.vibrate without a fresh tap), else the Web Vibration API.
export function vibrateAlert(pattern) {
  var p = pattern || [0, 200, 100, 200];
  try {
    if (window.AndroidBridge && typeof window.AndroidBridge.vibrate === 'function') { window.AndroidBridge.vibrate(p.join(',')); return; }
    if (navigator.vibrate) navigator.vibrate(p);
  } catch (e) { /* noop */ }
}

var API = process.env.REACT_APP_BACKEND_URL;

// Notification sound - short notification beep
var NOTIFICATION_SOUND_DATA = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWQACGrHs7+BcFRBR4B5cny0y8ObaFs7OUJvjZOrpIM2JDdWaHWLn7SsjVAcHkRXaW+HoLijdS8RKWh5a3eWuMOpbjUQK2hsZm2Oq7ehXRkQP2NkZXaEhYqjq6BxKgAgP01ZdJytvL+0gT0qLDVDaJWopqOKYFVJU2FdY4qjpZJoWFZbb2JlcYSPjnhfXmNudnp7d3h4d21tbXB0eX9/fXl3dHJwcnR4fH59e3h2dHJycnR3ent7enh3dXNycnR3e3x7eXd2dHNyc3V4e3x7eXd2dHNyc3V4e3x7eXd2dHNyc3V4e3x7eXd2dHNycw==';

// Generate ringtone audio as WAV blob URL
function generateRingtoneWav(type, sampleRate) {
  sampleRate = sampleRate || 44100;
  var duration = 2;
  var samples = sampleRate * duration;
  var data = new Float32Array(samples);

  switch (type) {
    case 'gentle':
      for (var i = 0; i < samples; i++) {
        var t = i / sampleRate;
        var env = Math.exp(-t * 2) * 0.4;
        data[i] = env * (Math.sin(2 * Math.PI * 523.25 * t) + 0.5 * Math.sin(2 * Math.PI * 659.25 * t));
      }
      break;
    case 'urgent':
      for (var j = 0; j < samples; j++) {
        var t2 = j / sampleRate;
        var pulse = Math.floor(t2 * 6) % 2 === 0 ? 1 : 0;
        data[j] = pulse * 0.35 * Math.sin(2 * Math.PI * 880 * t2);
      }
      break;
    case 'minimal':
      for (var k = 0; k < samples; k++) {
        var t3 = k / sampleRate;
        var beep = t3 < 0.15 || (t3 > 0.4 && t3 < 0.55) ? 1 : 0;
        data[k] = beep * 0.3 * Math.sin(2 * Math.PI * 1000 * t3);
      }
      break;
    default: // 'default'
      for (var m = 0; m < samples; m++) {
        var t4 = m / sampleRate;
        var ring = Math.sin(2 * Math.PI * 440 * t4) * (1 + 0.3 * Math.sin(2 * Math.PI * 20 * t4));
        var env2 = t4 < 0.8 ? 1 : Math.max(0, 1 - (t4 - 0.8) * 3);
        data[m] = ring * env2 * 0.35;
      }
  }

  // Convert to WAV blob URL
  var numChannels = 1;
  var bitsPerSample = 16;
  var byteRate = sampleRate * numChannels * (bitsPerSample / 8);
  var blockAlign = numChannels * (bitsPerSample / 8);
  var dataSize = samples * blockAlign;
  var buffer = new ArrayBuffer(44 + dataSize);
  var view = new DataView(buffer);

  var writeString = function(offset, str) { for (var idx = 0; idx < str.length; idx++) view.setUint8(offset + idx, str.charCodeAt(idx)); };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  for (var n = 0; n < samples; n++) {
    var s = Math.max(-1, Math.min(1, data[n]));
    view.setInt16(44 + n * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }

  var blob = new Blob([buffer], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}

// Default ringtone data URLs
var RINGTONE_DATA = 'data:audio/wav;base64,UklGRrYFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YZIFAACAf4B/gH+Af4B/gH+Af4B/gH9/f39/f39/f39/f39/f39/f35+fn5+fn5+fn5+fX19fX19fHx8fHx8fHx8fHt7e3t7e3t7e3p6enp6enp6eXl5eXl5eXl5eXh4eHh4eHh4d3d3d3d3d3d3dnZ2dnZ2dnZ1dXV1dXV1dHR0dHR0dHRzc3Nzc3NzcnJycnJycnJxcXFxcXFxcHBwcHBwcHBvb29vb29vbm5ubm5ubm5tbW1tbW1tbGxsbGxsbGtrawAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAH9/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/fn5+fn5+fn5+fn5+fX19fX19fX19fHx8fHx8fHx7e3t7e3t7e3t7enp6enp6enl5eXl5eXl5eHh4eHh4eHd3d3d3d3d2dnZ2dnZ2dXV1dXV1dHR0dHR0c3Nzc3NzcnJycnJycXFxcXFxcHBwcHBwb29vb29ubm5ubm1tbW1tbGxsbGtra2tra2pqampqaWlpaWloaGhoZ2dnZ2dnZmZmZmVlZWVkZGRkZGNjY2NiYmJiYWFhYWBgYGBfX19fXl5eXl5dXV1cXFxcW1tbW1paWlpZWVlZWFhYWFdXV1dWVlZWVVVVVFRUVFNTU1NSUlJSUVFRUVBQUFBPT09OTk5OTU1NTExMTEtLS0tKSkpKSUlJSEhISEdHR0dGRkZFRUVEREREQ0NDQkJCQkFBQUFAQEA/Pz8/Pj4+PT09PTw8PDw7Ozs6Ojo6OTk5ODg4ODc3NzY2NjY1NTU0NDQ0MzMzMjIyMjExMTAwMDAvLy8uLi4uLS0tLCwsLCsrKyoqKioqKSkoKCgoJycnJycmJiYlJSUlJCQkJCMjIyMiIiIhISEhICAgIB8fHx8eHh4dHR0dHBwcHBsbGxsaGhoaGRkZGBgYGBcXFxcWFhYVFRUVFBQUFBMTExISEhIREREREBAQDw8PDw4ODg0NDQ0MDAwLCwsLCgoKCgkJCQgICAcHBwcGBgYFBQUFBAQEAwMDAgICAgEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQECAgIDAwMDBAQEBQUFBgYGBwcHCAgICQkJCgoKCwsLDAwMDQ0NDg4ODw8PEBAQEREREhISExMTFBQUFRUVFhYWFxcXGBgYGRkZGhoaGxsbHBwcHR0dHh4eHx8fICAgISEhIiIiIyMjJCQkJSUlJiYmJycnKCgoKSkpKioqKysrLCwsLS0tLi4uLy8vMDAwMTExMjIyMzMzNDQ0NTU1NjY2Nzc3ODg4OTk5Ojo6Ozs7PDw8PT09Pj4+Pz8/QEBAQUFBQ0NDRERERUVFRkZGR0dHSEhISUlJSkpKS0tLTExMTU1NTk5OT09PUFBQUVFRUVJSU1NTU1RUVFVVVVZWV1dXWFhYWFlZWVpaWltbW1xcXF1dXV5eXl9fX19gYGBhYWFiYmJjY2NkZGRlZWVmZmZnZ2doaGhpaWlqampra2tsbGxtbW1ubm5vb29wcHBxcXFycnJzc3N0dHR1dXV2dnZ3d3d4eHh5eXl6enp7e3t8fHx9fX1+fn5/f3+AgA==';
var CALLING_TONE_DATA = 'data:audio/wav;base64,UklGRrYFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YZIFAACAf35+fX19fHx8e3t7enp6eXl5eHh4d3d3dnZ2dXV1dHR0c3Nzc3JycnFxcXBwcG9vb25ubm5tbW1sbGxsa2tramlpaWhoaGdnZ2ZmZmVlZWRkZGNjY2JiYmFhYWBgYF9fX15eXl1dXVxcXFtbW1paWllZWVhYWFdXV1ZWVlVVVVRUVFNTU1JSUlFRUVBQUE9PT05OTk1NTUxMTEtLS0pKSklJSUhISEdHR0ZGRkVFRURERENDQ0JCQkFBQUBAQD8/Pz4+Pj09PTw8PDs7Ozo6Ojk5OTg4ODc3NzY2NjU1NTQ0NDMzMzIyMjExMTAwMC8vLy4uLi0tLSwsLCsrKyoqKikpKSgoKCcnJyYmJiUlJSQkJCMjIyIiIiEhISAgIB8fHx4eHh0dHRwcHBsbGxoaGhkZGRgYGBcXFxYWFhUVFRQUFBMTExISEhERERAQEA8PDw4ODg0NDQwMDAsLCwoKCgkJCQgICAcHBwYGBgUFBQQEBAMDAwICAgEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQICAgMDAwQEBAUFBQYGBgcHBwgICAkJCQoKCgsLCwwMDA0NDQ4ODg8PDxAQEBERERAQEA==';

export function useChatNotifications(user, notificationPrefs) {
  var audioRef = useRef(null);
  var ringtoneRef = useRef(null);
  var callingToneRef = useRef(null);
  var chatToneRef = useRef(null);
  var ringtoneSettingsRef = useRef({
    incoming_ringtone: 'default',
    custom_ringtone_url: null,
    chat_notification_tone: 'default',
    custom_chat_tone_url: null
  });

  // Load ringtone settings and set up the correct audio
  var settingsLoadedRef = useRef(false);
  
  var loadRingtoneSettings = useCallback(async () => {
    var token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;
    
    try {
      var resp = await fetch(API + '/api/chat/ringtone-settings', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        var settings = await resp.json();
        ringtoneSettingsRef.current = settings;
        
        // --- Call ringtone ---
        if (settings.incoming_ringtone === 'custom' && settings.custom_ringtone_url) {
          ringtoneRef.current = new Audio(API + settings.custom_ringtone_url);
        } else if (settings.incoming_ringtone && settings.incoming_ringtone !== 'default') {
          var blobUrl = generateRingtoneWav(settings.incoming_ringtone);
          ringtoneRef.current = new Audio(blobUrl);
        } else {
          ringtoneRef.current = new Audio(RINGTONE_DATA);
        }
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 0.7;
        // Pre-load the audio so it's ready for instant playback
        ringtoneRef.current.load();

        // --- Chat notification tone ---
        if (settings.chat_notification_tone === 'custom' && settings.custom_chat_tone_url) {
          chatToneRef.current = new Audio(API + settings.custom_chat_tone_url);
        } else if (settings.chat_notification_tone && settings.chat_notification_tone !== 'default') {
          var chatBlobUrl = generateRingtoneWav(settings.chat_notification_tone);
          chatToneRef.current = new Audio(chatBlobUrl);
        } else {
          chatToneRef.current = new Audio(NOTIFICATION_SOUND_DATA);
        }
        chatToneRef.current.volume = 0.5;
        chatToneRef.current.load();
        
        settingsLoadedRef.current = true;
      }
    } catch (e) {
      console.log('Using default ringtone');
    }
  }, []);

  // Initialize audio elements
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_DATA);
    audioRef.current.volume = 0.5;
    
    ringtoneRef.current = new Audio(RINGTONE_DATA);
    ringtoneRef.current.loop = true;
    ringtoneRef.current.volume = 0.7;
    
    callingToneRef.current = new Audio(CALLING_TONE_DATA);
    callingToneRef.current.loop = true;
    callingToneRef.current.volume = 0.5;
    
    chatToneRef.current = new Audio(NOTIFICATION_SOUND_DATA);
    chatToneRef.current.volume = 0.5;
    
    // Load custom ringtone settings
    loadRingtoneSettings();
  }, [loadRingtoneSettings]);

  // Listen for ringtone settings changes (from RingtoneSettings component)
  useEffect(() => {
    var handler = function() { loadRingtoneSettings(); };
    window.addEventListener('ringtone-settings-changed', handler);
    return function() { window.removeEventListener('ringtone-settings-changed', handler); };
  }, [loadRingtoneSettings]);

  // Play chat notification sound (uses chat-specific tone)
  var playNotificationSound = useCallback(() => {
    if (notificationPrefs.sound_enabled) {
      var tone = chatToneRef.current || audioRef.current;
      if (tone) {
        tone.currentTime = 0;
        tone.play().catch(function() {});
      }
    }
    if (notificationPrefs.vibrate_enabled) vibrateAlert();
  }, [notificationPrefs.sound_enabled, notificationPrefs.vibrate_enabled]);

  // Show browser notification
  var showBrowserNotification = useCallback(function(message, conversation) {
    if (!notificationPrefs.enabled) return;
    if (message.sender_id === (user && user.user_id)) return;
    
    var isSupport = conversation && conversation.is_support_chat;
    var isGroup = conversation && conversation.conversation_type === 'group';
    var isDirect = conversation && conversation.conversation_type === 'direct';
    
    if (isSupport && !notificationPrefs.support_messages) return;
    if (isGroup && !notificationPrefs.group_messages) return;
    if (isDirect && !notificationPrefs.direct_messages) return;

    playNotificationSound();

    if (document.visibilityState === 'visible' && window.location.pathname === '/staff/chat') {
      return;
    }

    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    var title = message.sender_name || 'New Message';
    if (isSupport) title = 'Support: ' + message.sender_name;
    if (isGroup) title = (conversation.name || 'Group') + ': ' + message.sender_name;

    try {
      var notification = new Notification(title, {
        body: (message.content && message.content.substring(0, 100)) || 'New message',
        icon: '/favicon.ico',
        tag: 'chat-' + message.message_id,
        silent: true,
      });

      notification.onclick = function() {
        window.focus();
        window.location.href = '/staff/chat';
        notification.close();
      };

      setTimeout(function() { notification.close(); }, 5000);
    } catch (e) {}
  }, [notificationPrefs, user, playNotificationSound]);

  // Start/stop ringtones
  var startRingtone = useCallback(function(type) {
    try {
      // If settings haven't loaded yet, try loading now
      if (!settingsLoadedRef.current) {
        loadRingtoneSettings();
      }
      
      if (type === 'incoming' && ringtoneRef.current) {
        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current.play().catch(function(e) { console.log('Ringtone play failed:', e); });
      } else if (type === 'outgoing' && callingToneRef.current) {
        callingToneRef.current.currentTime = 0;
        callingToneRef.current.play().catch(function(e) { console.log('Calling tone play failed:', e); });
      }
    } catch (error) {
      console.error('Error playing ringtone:', error);
    }
  }, [loadRingtoneSettings]);

  var stopRingtones = useCallback(function() {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
      if (callingToneRef.current) {
        callingToneRef.current.pause();
        callingToneRef.current.currentTime = 0;
      }
    } catch (error) {
      console.error('Error stopping ringtones:', error);
    }
  }, []);

  return {
    playNotificationSound,
    showBrowserNotification,
    startRingtone,
    stopRingtones
  };
}
